<script lang="ts" setup>
import { ref, inject, onMounted, onBeforeUnmount, watch } from 'vue';
import { ViewerEvents } from '@immersive-tech/viewer-engine';
import PartConfig from '@/components/Parts/PartConfig.vue';
import CollapseFieldset from '@/components/CollapseFieldset.vue';

import type { Viewer, IViewerModelDownloadCompleteEvent, IViewerModelDestroyedEvent, Nullable } from '@immersive-tech/viewer-engine';
import type { Ref } from 'vue';
import type { IManifest, IManifestPart } from '@/types/IManifest';

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const manifest = inject('manifest') as Ref<IManifest>;
const parts = ref<IManifestPart[]>([]);
const pendingParts = ref<string[]>([]);
const eventsBound = ref(false);

const modelDownloadedHandler = (event: CustomEvent<IViewerModelDownloadCompleteEvent>) => {
  const pendingPartIndex = pendingParts.value.indexOf(event.detail.entityIdentifier);

  if (pendingPartIndex !== -1) {
    pendingParts.value.splice(pendingPartIndex, 1);
  }
};

const modelDestroyedHandler = (event: CustomEvent<IViewerModelDestroyedEvent>) => {
  const existingPart = parts.value.find((part) => part.name === event.detail.data) as IManifestPart | undefined;

  if (existingPart) {
    parts.value.splice(parts.value.indexOf(existingPart), 1);
  }
};

const mountHandler = () => {
  if (!viewer.value) return;
  if (eventsBound.value === true) return;

  viewer.value.addEventListener(ViewerEvents.MODEL_DOWNLOAD_COMPLETE, modelDownloadedHandler as EventListener);
  viewer.value.addEventListener(ViewerEvents.MODEL_DESTROYED, modelDestroyedHandler as EventListener);
};

const beforeUnmountHandler = () => {
  if (!viewer.value) return;

  viewer.value.removeEventListener(ViewerEvents.MODEL_DOWNLOAD_COMPLETE, modelDownloadedHandler as EventListener);
  viewer.value.removeEventListener(ViewerEvents.MODEL_DESTROYED, modelDestroyedHandler as EventListener);
};

const viewerWatcher = (newValue: Nullable<Viewer>) => {
  if (!newValue) return;
  if (eventsBound.value === true) return;

  newValue.addEventListener(ViewerEvents.MODEL_DOWNLOAD_COMPLETE, modelDownloadedHandler as EventListener);
  newValue.addEventListener(ViewerEvents.MODEL_DESTROYED, modelDestroyedHandler as EventListener);
};

const manifestWatcher = (newManifest: IManifest, oldManifest: IManifest) => {
  if (JSON.stringify(newManifest) === JSON.stringify(oldManifest)) return;

  parts.value = newManifest.parts;
  pendingParts.value = newManifest.parts.map<string>((part) => part.path);
};

watch(viewer, viewerWatcher);
watch(manifest, manifestWatcher);
onMounted(mountHandler);
onBeforeUnmount(beforeUnmountHandler);
</script>

<template>
  <CollapseFieldset :enable-collapse="false">
    <template #legend>
      Parts
    </template>

    <div class="flex flex-col">
      <span v-if="manifest.parts.length === 0" class="text-xs">No parts loaded</span>

      <CollapseFieldset v-for="part in manifest.parts" :key="part.path" :enable-collapse="true" class="border-b last-of-type:border-none">
        <template #legend>
          {{ part.path }}
        </template>

        <p v-if="part.path && pendingParts.includes(part.path)" class="text-xs text-red-400 text-center mb-1">
          Part Not Loaded
        </p>

        <PartConfig :identifier="part.path" @update="null" @delete="null" />
      </CollapseFieldset>
    </div>
  </CollapseFieldset>
</template>
